/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import React, { useState, useEffect } from "react"
import { Modal, Button, Tag, Typography, Spin, message, Empty } from "antd"
import {
  HolderOutlined,
  FolderOutlined,
  FileTextOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { setService } from "@/services/dictionary/setApi"

const { Text } = Typography

interface SystemSetReorderModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  parentId: string | number
  currentFolderName: string
}

interface SortableItemProps {
  item: any
  index: number
  totalCount: number
  onMoveUp: () => void
  onMoveDown: () => void
}

const SortableItem: React.FC<SortableItemProps> = ({
  item,
  index,
  totalCount,
  onMoveUp,
  onMoveDown,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 9999 : "auto",
    opacity: isDragging ? 0.75 : 1,
  }

  const isFolder = item.node_type === "folder"

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center justify-between gap-3 p-2.5 mb-2 rounded-lg border bg-white transition-shadow ${
        isDragging
          ? "border-primary shadow-lg bg-blue-50/50"
          : "border-gray-200 hover:border-gray-300 hover:shadow-xs"
      }`}
    >
      {/* Left: Drag handle & index & info */}
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div
          {...attributes}
          {...listeners}
          className="flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing rounded hover:bg-gray-100"
          title="Kéo thả để đổi vị trí"
        >
          <HolderOutlined className="text-base" />
        </div>

        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-600">
          {index + 1}
        </div>

        <div className="shrink-0 text-base">
          {isFolder ? (
            <FolderOutlined className="text-blue-500" />
          ) : (
            <FileTextOutlined className="text-green-600" />
          )}
        </div>

        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-gray-800 text-sm truncate max-w-[280px]">
              {item.title}
            </span>
            <Tag color={isFolder ? "blue" : "green"} className="text-[10px] m-0 py-0 px-1">
              {isFolder ? "Thư mục" : "Bộ từ"}
            </Tag>
            {Array.isArray(item.tags) && item.tags.map((t: string) => (
              <Tag key={t} color="cyan" className="text-[10px] m-0 py-0 px-1 font-semibold">
                {t}
              </Tag>
            ))}
          </div>
          {item.full_title && item.full_title !== item.title && (
            <Text type="secondary" className="text-xs italic truncate max-w-[320px]">
              {item.full_title}
            </Text>
          )}
        </div>
      </div>

      {/* Right: Quick Up/Down buttons */}
      <div className="flex items-center gap-1 shrink-0">
        <Button
          type="text"
          size="small"
          icon={<ArrowUpOutlined />}
          disabled={index === 0}
          onClick={(e) => {
            e.stopPropagation()
            onMoveUp()
          }}
          title="Di chuyển lên"
          className="text-gray-500 hover:text-primary"
        />
        <Button
          type="text"
          size="small"
          icon={<ArrowDownOutlined />}
          disabled={index === totalCount - 1}
          onClick={(e) => {
            e.stopPropagation()
            onMoveDown()
          }}
          title="Di chuyển xuống"
          className="text-gray-500 hover:text-primary"
        />
      </div>
    </div>
  )
}

export const SystemSetReorderModal: React.FC<SystemSetReorderModalProps> = ({
  open,
  onClose,
  onSuccess,
  parentId,
  currentFolderName,
}) => {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // Tránh kích hoạt nhầm khi click
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  useEffect(() => {
    if (open) {
      fetchItems()
    }
  }, [open, parentId])

  const fetchItems = async () => {
    setLoading(true)
    try {
      const res = await setService.list({
        parent_id: parentId,
        limit: 1000,
        page: 1,
        type: "system",
      })
      if (res.code === 200 && res.data) {
        setItems(res.data)
      } else {
        message.error(res.message || "Không thể tải danh sách bộ từ")
      }
    } catch (error: any) {
      message.error(error.message || "Lỗi khi tải danh sách bộ từ")
    } finally {
      setLoading(false)
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setItems((prevItems) => {
        const oldIndex = prevItems.findIndex((it) => it.id === active.id)
        const newIndex = prevItems.findIndex((it) => it.id === over.id)
        return arrayMove(prevItems, oldIndex, newIndex)
      })
    }
  }

  const handleMoveUp = (index: number) => {
    if (index > 0) {
      setItems((prev) => arrayMove(prev, index, index - 1))
    }
  }

  const handleMoveDown = (index: number) => {
    if (index < items.length - 1) {
      setItems((prev) => arrayMove(prev, index, index + 1))
    }
  }

  const handleSave = async () => {
    if (items.length === 0) {
      onClose()
      return
    }

    setSaving(true)
    try {
      const payload = items.map((item, index) => ({
        id: item.id,
        order_index: index + 1,
      }))

      const res = await setService.reorder(payload)
      if (res.code === 200) {
        message.success("Cập nhật thứ tự thành công")
        onSuccess()
        onClose()
      } else {
        message.error(res.message || "Cập nhật thứ tự thất bại")
      }
    } catch (error: any) {
      message.error(error.message || "Đã xảy ra lỗi khi lưu thứ tự")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <span>Sắp xếp thứ tự</span>
          <span className="text-xs font-normal text-gray-500">
            ({currentFolderName})
          </span>
        </div>
      }
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Lưu thứ tự"
      cancelText="Hủy"
      confirmLoading={saving}
      width={560}
      destroyOnClose
    >
      <div className="my-3">
        <Text type="secondary" className="text-xs block mb-3">
          💡 <strong>Hướng dẫn:</strong> Giữ chuột vào biểu tượng <HolderOutlined /> để kéo thả dòng, hoặc bấm nút mũi tên <ArrowUpOutlined /> / <ArrowDownOutlined /> để thay đổi thứ tự. Sau đó bấm <strong>Lưu thứ tự</strong>.
        </Text>

        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <Spin tip="Đang tải danh sách..." />
          </div>
        ) : items.length === 0 ? (
          <Empty description="Thư mục này hiện chưa có mục nào" className="py-8" />
        ) : (
          <div className="max-h-[60vh] overflow-y-auto pr-1">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items.map((it) => it.id)}
                strategy={verticalListSortingStrategy}
              >
                {items.map((item, idx) => (
                  <SortableItem
                    key={item.id}
                    item={item}
                    index={idx}
                    totalCount={items.length}
                    onMoveUp={() => handleMoveUp(idx)}
                    onMoveDown={() => handleMoveDown(idx)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default SystemSetReorderModal
